/**
 * AppLogo — brand logo component.
 *
 * Single logo source: src/assets/branding/logo-icon.png
 * No theme-switching — one asset works across light and dark modes.
 *
 * Variants:
 *   "full"     — logo image at natural aspect ratio. Use for login page and
 *                loading screen where the logo is a centrepiece.
 *   "icon"     — square crop (width === height). Use for collapsed sidebar,
 *                favicon contexts and compact avatar-sized slots.
 *   "wordmark" — square icon image + "After Summit" text side-by-side. Use
 *                for expanded sidebar header and dropdown branding strip.
 *
 * Canonical sizes (per brand spec):
 *   Login page      → height={140}  variant="full"
 *   Loading screen  → height={100}  variant="full"
 *   Sidebar expanded→ height={48}   variant="wordmark"
 *   Sidebar collapsed→ height={38}  variant="icon"
 *   Header mobile   → height={44}   variant="icon"
 *   Dropdown strip  → height={32}   variant="icon"
 */

import logoIconSrc from '@assets/branding/logo-icon.png'

export function AppLogo({ height = 36, variant = 'full', className = '', style = {} }) {

  // ── Icon mark — square slot ────────────────────────────────────────────────
  if (variant === 'icon') {
    return (
      <img
        src={logoIconSrc}
        alt="After Summit"
        style={{
          height:     `${height}px`,
          width:      `${height}px`,
          display:    'block',
          objectFit:  'contain',
          flexShrink: 0,
          ...style,
        }}
        className={className}
        draggable={false}
      />
    )
  }

  // ── Wordmark — square icon + text row ──────────────────────────────────────
  if (variant === 'wordmark') {
    const fontSize = Math.round(height * 0.42)
    const gap      = Math.round(height * 0.25)

    return (
      <div
        className={className}
        style={{
          display:    'flex',
          alignItems: 'center',
          gap:        `${gap}px`,
          flexShrink: 0,
          ...style,
        }}
      >
        <img
          src={logoIconSrc}
          alt=""
          aria-hidden
          style={{
            height:     `${height}px`,
            width:      `${height}px`,
            display:    'block',
            objectFit:  'contain',
            flexShrink: 0,
          }}
          draggable={false}
        />
        <span
          style={{
            fontFamily:    'var(--ac-font-display)',
            fontWeight:    700,
            fontSize:      `${fontSize}px`,
            color:         'var(--ac-text-base)',
            letterSpacing: '-0.025em',
            lineHeight:    1,
            userSelect:    'none',
            whiteSpace:    'nowrap',
          }}
        >
          After{' '}
          <span style={{ color: 'var(--ac-secondary)' }}>Summit</span>
        </span>
      </div>
    )
  }

  // ── Full — natural aspect ratio ────────────────────────────────────────────
  return (
    <img
      src={logoIconSrc}
      alt="After Summit"
      style={{
        height:     `${height}px`,
        width:      'auto',
        display:    'block',
        objectFit:  'contain',
        flexShrink: 0,
        ...style,
      }}
      className={className}
      draggable={false}
    />
  )
}
